import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * The canonical Groupardy brand lockup — the official SVG mark + the
 * "Groupardy" wordmark set in Helvetica Inserat (the brand font per
 * brand-book.html) with the layered teal/gold drop-shadow. This is the single
 * brand-logo component for the whole app; use it (not a one-off <img> or a
 * hand-rolled wordmark) wherever the logo appears.
 *
 * Props:
 *   size:        "xs" | "sm" | "md" | "lg" | "xl"  (default "md")
 *   variant:     "light" | "dark" | "teal"          (wordmark color; default "light")
 *   orientation: "horizontal" | "vertical"          (default "horizontal")
 *   showWordmark: bool — mark + wordmark, or mark only (default true)
 *   shadow:      bool — layered teal/gold text-shadow on the wordmark (default true)
 *
 * Also exports <BrandMark> (mark only) and <BrandWordmark> (wordmark only) for
 * surfaces that compose the two separately (e.g. the loading spinner).
 */

const LOGO_SRC = "/g2-logo.svg";

const SIZE_PRESETS = {
  xs: {
    mark: 24,
    wordmark: "0.7rem",
    gap: 6,
    shadow: "1px 1px 0 rgba(46,196,182,0.5), 2px 2px 0 rgba(255,224,102,0.26)",
  },
  sm: {
    mark: 38,
    wordmark: "1.1rem",
    gap: 9,
    shadow: "2px 2px 0 rgba(46,196,182,0.5), 3.5px 3.5px 0 rgba(255,224,102,0.26)",
  },
  md: {
    mark: 56,
    wordmark: "1.4rem",
    gap: 10,
    shadow: "2px 2px 0 rgba(46,196,182,0.55), 4px 4px 0 rgba(255,224,102,0.28)",
  },
  lg: {
    mark: 96,
    wordmark: "2.2rem",
    gap: 14,
    shadow: "3px 3px 0 rgba(46,196,182,0.55), 6px 6px 0 rgba(255,224,102,0.28)",
  },
  xl: {
    mark: 128,
    wordmark: "2.8rem",
    gap: 16,
    shadow: "4px 4px 0 rgba(46,196,182,0.55), 8px 8px 0 rgba(255,224,102,0.28)",
  },
};

const VARIANT_COLORS = {
  light: "#1c1c1c",
  dark: "#ffffff",
  teal: "#ffffff",
};

const getPreset = (size) => SIZE_PRESETS[size] || SIZE_PRESETS.md;
const getColor = (variant) => VARIANT_COLORS[variant] || VARIANT_COLORS.light;

const Root = styled(Box, {
  shouldForwardProp: (p) => p !== "vertical" && p !== "lockupGap",
})(({ vertical, lockupGap }) => ({
  display: "inline-flex",
  flexDirection: vertical ? "column" : "row",
  alignItems: "center",
  gap: lockupGap,
  lineHeight: 1,
}));

const MarkImg = styled("img", {
  shouldForwardProp: (p) => p !== "markSize",
})(({ markSize }) => ({
  width: markSize,
  height: markSize,
  display: "block",
  flexShrink: 0,
  userSelect: "none",
}));

const WordmarkText = styled(Typography, {
  shouldForwardProp: (p) =>
    p !== "wordmarkSize" && p !== "wordmarkColor" && p !== "wordmarkShadow",
})(({ wordmarkSize, wordmarkColor, wordmarkShadow }) => ({
  fontFamily: '"Helvetica Inserat", Impact, "Arial Black", sans-serif',
  fontSize: wordmarkSize,
  lineHeight: 1,
  letterSpacing: "0.18em",
  color: wordmarkColor,
  fontWeight: 800,
  textTransform: "uppercase",
  textShadow: wordmarkShadow || "none",
  whiteSpace: "nowrap",
  paddingTop: "0.05em",
}));

const BrandMark = ({ size = "md", alt = "Groupardy", className, sx }) => (
  <MarkImg
    src={LOGO_SRC}
    alt={alt}
    markSize={getPreset(size).mark}
    draggable={false}
    className={className}
    sx={sx}
  />
);

const BrandWordmark = ({
  size = "md",
  variant = "light",
  shadow = true,
  className,
  sx,
}) => {
  const preset = getPreset(size);
  return (
    <WordmarkText
      component="span"
      wordmarkSize={preset.wordmark}
      wordmarkColor={getColor(variant)}
      wordmarkShadow={shadow ? preset.shadow : null}
      className={className}
      sx={sx}
    >
      Groupardy
    </WordmarkText>
  );
};

const BrandLockup = ({
  size = "md",
  variant = "light",
  orientation = "horizontal",
  showWordmark = true,
  shadow = true,
  className,
  sx,
}) => {
  const preset = getPreset(size);
  const vertical = orientation === "vertical";

  return (
    <Root vertical={vertical} lockupGap={preset.gap} className={className} sx={sx}>
      <BrandMark size={size} alt={showWordmark ? "" : "Groupardy"} />
      {showWordmark && (
        <BrandWordmark size={size} variant={variant} shadow={shadow} />
      )}
    </Root>
  );
};

export default BrandLockup;
export { BrandLockup, BrandMark, BrandWordmark };
